// Condensed from the gold-set reviewer guide (data-ingestion repo,
// .jupyter/data/gold_set/REVIEWER_INSTRUCTIONS.md). Static on purpose: keep it in step by hand.
// Backticks mark codes and category labels.

export type RuleItem = { term?: string; text: string }

export type RuleSection = { title: string; intro?: string; items: RuleItem[] }

export type CategoryCheck = { considering: string; notThese: string }

export const REVIEW_STEPS: RuleItem[] = [
  {
    term: 'Read every page',
    text: "Many PDFs hold more than one letter: a payer response with Natera's appeal attached, a fax cover, a portal printout.",
  },
  {
    term: 'More than one letter?',
    text: 'The latest-dated letter decides the category. Set More than one letter to Yes.',
  },
  {
    term: 'Who wrote the latest letter?',
    text: "A payer: classify what the payer says. Natera's own appeal letter (Natera letterhead, PO BOX 889027 Los Angeles) with no later payer page responding to it: the payer hasn't acted, so the category is `appeal_rejected`.",
  },
  {
    term: 'Phase',
    text: 'claim, claim appeal, prior auth, prior-auth appeal, billing or other. The tests are below.',
  },
  {
    term: 'Outcome and reason',
    text: 'pick the most specific category the letter supports.',
  },
  {
    term: 'Fill in the form',
    text: 'category, Sure?, More than one letter?, and one line in Notes whenever you are unsure, on what made it hard.',
  },
  {
    term: 'More than 10 minutes on one document?',
    text: 'Set Sure? to No, write why, and move on.',
  },
]

export const LABEL_FROM_PDF_RULE =
  "Label from the PDF alone, not from what you know about the patient's case, the claim history or other systems."

export const REVIEW_RULE_SECTIONS: RuleSection[] = [
  {
    title: 'Prior auth or claim?',
    intro: 'Decide by what the decision applies to, not by single words.',
    items: [
      {
        term: 'Prior auth',
        text: 'a request for a service not yet billed. Signs: an authorization or reference number with no billed amount; "valid from / to" dates, or "this is not a guarantee of payment"; "requested service", "does not require authorization"; letterhead from a review company: eviCore, Carelon/AIM, Avalon, Acentra.',
      },
      {
        term: 'Claim',
        text: 'a billed service already performed. Signs: a claim number with a past date of service and a billed amount; an EOB or remittance grid (Billed, Allowed, Patient responsibility, Paid); remark codes.',
      },
      {
        text: 'A claim denial that mentions authorization ("authorization required", "no authorization on file") is still a claim.',
      },
      {
        text: 'A refused retro-authorization ("we cannot authorize services more than 1 year old") is still prior auth.',
      },
      {
        term: 'Appeals',
        text: 'a Carelon or other review-company letter with "Non-Authorized", "Criteria Not Met" or "Claim No.: N/A" is a prior-auth appeal. Label by the letter, and write `PA/claim?` in Notes if your normal practice would differ.',
      },
    ],
  },
  {
    title: 'Appeal outcomes',
    items: [
      {
        term: 'Acknowledged',
        text: 'the payer received the appeal and is reviewing it. "We have forwarded your request to X; they will send a decision" is still acknowledged.',
      },
      {
        term: 'Acknowledged, additional information needed',
        text: 'the case is open, and the payer will continue once it gets forms, signatures, consent or corrected materials. Use the medical-records version when it asks for medical records.',
      },
      {
        term: 'Rejected',
        text: 'the appeal was not accepted, is "closed", "cannot be processed" or "not eligible", or must be sent somewhere else ("please redirect your mailing", "file a new appeal"). This holds even when the letter invites you to resubmit, and when it says the appeal "will not be treated as an appeal".',
      },
      {
        term: 'Upheld',
        text: 'the payer reviewed it and the denial stands. An "Administrative Uphold" is upheld, not acknowledged.',
      },
      {
        term: 'Overturned',
        text: 'the payer reversed its decision after an appeal. An EOB that only shows a payment, with no statement that a decision was reversed, is a claim document. Write `EOB only` in Notes if you think it reflects an overturned appeal.',
      },
    ],
  },
  {
    title: 'Use the specific reason',
    items: [
      {
        text: 'When the letter states why, use the specific category: no prior authorization, medical necessity, experimental, out of network, not covered (a benefit exclusion: "not a covered benefit", "excluded"), timely filing, coding, medical records, duplicate, eligibility.',
      },
      { text: 'Use "other" or the plain category only when no reason is given or none fits.' },
      { text: 'A checked box counts as the payer saying it. An unchecked box does not.' },
    ],
  },
  {
    title: "When it doesn't fit",
    items: [
      {
        term: 'No category fits',
        text: 'choose the closest, set Sure? to No, and say what is missing.',
      },
      { term: 'Unreadable page', text: 'write `illegible` in Notes.' },
      {
        term: 'Two letters with the same latest date',
        text: "follow the payer's decision letter over an acknowledgement, and write `same date` in Notes.",
      },
    ],
  },
]

export const CATEGORY_CHECKS: CategoryCheck[] = [
  {
    considering: '`prior_authorization_approved`',
    notThese:
      '`prior_authorization_no_authorization_required` (nothing to approve), `prior_authorization_multiple_decisions` (some services approved, others denied), `prior_authorization_cancellation_*` (voided or duplicate request), or a claim EOB',
  },
  {
    considering: '`prior_authorization_denied_medical_necessity`',
    notThese:
      '`…_denied_non_covered` (a benefit exclusion), `…_denied_out_of_network`, `…_denied_other`, `…_multiple_decisions`, or a pre-service request for more information',
  },
  {
    considering: '`prior_authorization_request_additional_info_needed`',
    notThese:
      '`prior_authorization_request_other` (an administrative ask: signature, form, demographics), or `…_cancellation_lack_of_information` (already closed)',
  },
  {
    considering: '`prior_authorization_denied_out_of_network`',
    notThese:
      '`…_denied_other` or `…_denied_non_covered`; out of network must be the stated reason',
  },
  {
    considering: '`prior_authorization_no_authorization_required`',
    notThese: '`prior_authorization_approved`',
  },
  {
    considering: '`appeal_acknowledged`',
    notThese:
      '`appeal_acknowledged_additional_info_needed` (something is requested), `appeal_rejected` (closed or redirected), or a decision letter',
  },
  {
    considering:
      '`appeal_denied_upheld_no_prior_authorization`, `…_experimental`, `…_out_of_network`',
    notThese:
      'the other upheld reasons, `appeal_rejected` (not reviewed on the merits, e.g. filed too late), or the prior-auth appeal versions',
  },
  {
    considering: '`appeal_overturned`',
    notThese: 'a claim EOB or payment, or `prior_auth_appeal_overturned`',
  },
  {
    considering: '`claim_acknowledged`',
    notThese: '`claim_request_*` (the payer wants records or information) or a claim EOB',
  },
]
